import { InputField } from "@/components/InputField";
import Spinner from "@/components/atoms/Spinner/Spinner";
import SmallPlayer from "@/components/molecules/Player/FC24/SmallPlayer";
import { pitchCardPlaceholder } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { SearchedPlayer } from "@/redux/players/SearchedPlayer.type";
import { useTranslation } from "@repo/shared/i18n";
import React from "react";

import SectionList from "./SectionListComponent";

export type IncludeExcludeSectionType =
  | "include"
  | "exclude"
  | "squad-builder-include";

const IncludeExcludeSection = ({
  type,
  icon,
  players,
  showSearch,
  searchTerm,
  setSearchTerm,
  setShowSearch,
  isSearching,
  result,
  onAdd,
  onRemove,
  showAll,
  setShowAll,
  isMobile,
  handleClickedAdd,
  cantIncludePlayers,
}: {
  type: IncludeExcludeSectionType;
  icon: React.ReactNode;
  players: any[];
  showSearch: IncludeExcludeSectionType | undefined;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  setShowSearch: React.Dispatch<
    React.SetStateAction<IncludeExcludeSectionType | undefined>
  >;
  isSearching: boolean;
  result: SearchedPlayer[];
  onAdd: (player: SearchedPlayer, type: IncludeExcludeSectionType) => void;
  onRemove: (type: IncludeExcludeSectionType, resourceId: number) => void;
  showAll: boolean;
  setShowAll: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAllSectionType: React.Dispatch<
    React.SetStateAction<IncludeExcludeSectionType | null>
  >;
  isMobile: boolean;
  handleClickedAdd: () => void;
  cantIncludePlayers: boolean;
}) => {
  const { t } = useTranslation(["sbcs", "common"]);
  const firstTwo = players.slice(0, 2);
  const remaining = players.slice(2);
  const isSearchActive = showSearch === type;

  const resultsBar =
    isSearchActive && !isSearching && result?.length > 0 ? (
      <>
        {isSearching ? <Spinner /> : null}
        <div className="my-2 rounded-lg bg-panel-foreground p-2">
          <span className="text-dark-tertiary text-base">
            {t("includeExclude.results")}
          </span>
          <div className="flex flex-wrap gap-2 w-fit mx-auto justify-start items-start p-1">
            {result?.map((p) => (
              <button key={p.resourceId} onClick={() => onAdd(p, type)}>
                <SmallPlayer
                  {...p}
                  isEvolvedPlayer={p.evoPathId !== undefined}
                />
              </button>
            ))}
          </div>
        </div>
      </>
    ) : null;

  return (
    <SectionList.Section
      text={
        type === "include"
          ? t("includeExclude.mustIncludePlayers")
          : t("includeExclude.excludePlayers")
      }
      icon={icon}
      searchbar={
        showSearch === type ? (
          <>
            <div>
              <InputField
                showSearch={true}
                type="search"
                autoFocus={true}
                value={searchTerm}
                onChange={setSearchTerm}
                dismissSearch={() => setShowSearch(undefined)}
                placeholder={t("includeExclude.searchPlaceholder")}
              />
            </div>
            <div className={"my-2 rounded-lg bg-[#333A42]"}>
              {isSearchActive && isSearching && (
                <div className="flex justify-center">
                  <Spinner />
                </div>
              )}
            </div>
            <div className={"my-2 rounded-lg bg-[#333A42]"}>
              {isSearchActive && !isSearching && result?.length === 0 && (
                <p className="w-full p-4 text-center text-white">
                  {t("includeExclude.noPlayersFound", { name: searchTerm })}
                </p>
              )}
            </div>
          </>
        ) : null
      }
      searchResults={resultsBar}
    >
      <div className="flex flex-col p-3">
        <div className="flex justify-between">
          {players.length === 0 && (
            <div className="w-[70px] text-panel-base">
              {pitchCardPlaceholder}
            </div>
          )}
          <div className={cn("flex gap-1", isMobile && "gap-5")}>
            {firstTwo.map((player) => (
              <button
                key={player.resourceId}
                onClick={() => onRemove(type, player.resourceId)}
              >
                <SmallPlayer
                  {...player}
                  isEvolvedPlayer={player.evoPathId !== undefined}
                />
              </button>
            ))}
          </div>
          <div className="flex flex-col justify-end min-h-22 pl-1 relative touch-device:text-sm">
            {remaining.length > 0 && (
              <div className="absolute bottom-[42px]">
                <button
                  className="h-[80px] w-[90px] bg-action-tertiary rounded shadow-soft-dark-bottom text-dark-secondary hover:bg-action-tertiary-hover"
                  onClick={() => setShowAll((prev) => !prev)}
                >
                  <div className="flex flex-col justify-center items-center">
                    {!showAll && (
                      <div className="px-2 py-0.5 bg-[#0576C7] rounded-3xl">{`+${players.length - 2}`}</div>
                    )}
                    <div>
                      {showAll
                        ? t("common:showLessLower")
                        : t("common:showAll")}
                    </div>
                  </div>
                </button>
              </div>
            )}
            {cantIncludePlayers && (
              <span className="text-xs mb-1 absolute top-0 w-40 text-right right-0 text-dark-secondary">
                {t("includeExclude.freeLimit")}
              </span>
            )}
            <button
              className="h-9 w-22.5 bg-action-tertiary rounded shadow-soft-dark-bottom text-dark-secondary hover:bg-action-tertiary-hover"
              onClick={(e) => {
                e.stopPropagation();
                !cantIncludePlayers && handleClickedAdd();
              }}
            >
              {t("includeExclude.add")}
            </button>
          </div>
        </div>
        {showAll && remaining.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mt-3">
            {remaining.map((player) => (
              <button
                className="flex flex-wrap gap-2 w-fit mx-auto justify-start items-start"
                key={player.resourceId}
                onClick={() => onRemove(type, player.resourceId)}
              >
                <SmallPlayer
                  {...player}
                  isEvolvedPlayer={player.evoPathId !== undefined}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </SectionList.Section>
  );
};

export default IncludeExcludeSection;
